import { useCallback, useContext, useEffect } from "react";
import { RendererContext } from "../contexts/rendererContext";
import { Lashing } from "../../../sjorcraft3d/src/objects/lashings/lashing";
import { SquareLashing } from "../../../sjorcraft3d/src/objects/lashings/squareLashing";
import { BipodLashing } from "../../../sjorcraft3d/src/objects/lashings/bipodLashing";
import { TripodLashing } from "../../../sjorcraft3d/src/objects/lashings/tripodLashing";
import { ScaffoldLashing } from "../../../sjorcraft3d/src/objects/lashings/scaffoldLashing";
import { Pole } from "../../../sjorcraft3d/src/objects/pole";
import { useMutation } from "@apollo/client";
import {
  ADD_ITEMS,
  DELETE_ITEMS,
  EDIT_BIPOD_LASHING,
  EDIT_LASHING,
  EDIT_SCAFFOLD_LASHING,
  EDIT_TRIPOD_LASHING,
  PROJECT_FRAGMENT,
} from "../graphql/gqlProject";
import { useParams } from "react-router-dom";
import client from "../apollo/apolloClient";

export const useLashings = () => {
  const rendererContext = useContext(RendererContext);
  const viewer = rendererContext.viewer;
  const [addItems] = useMutation(ADD_ITEMS);
  const [deleteItems] = useMutation(DELETE_ITEMS);
  const [editLashing] = useMutation(EDIT_LASHING);
  const [editBipodLashing] = useMutation(EDIT_BIPOD_LASHING);
  const [editTripodLashing] = useMutation(EDIT_TRIPOD_LASHING);
  const [editScaffoldLashing] = useMutation(EDIT_SCAFFOLD_LASHING);
  const { projectId } = useParams();

  const onItemsPlaced = useCallback(
    ({
      poles: poles,
      squareLashings,
      bipodLashings,
      tripodLashings,
      scaffoldLashings,
    }: {
      poles: Pole[];
      squareLashings: SquareLashing[];
      bipodLashings: BipodLashing[];
      tripodLashings: TripodLashing[];
      scaffoldLashings: ScaffoldLashing[];
    }) => {
      const project = client.readFragment({
        fragment: PROJECT_FRAGMENT,
        id: `Project:${projectId}`,
        fragmentName: "Project",
      });
      if (!project) return;

      const newPoles = poles.map((pole) => {
        return {
          identifier: pole.identifier,
          position: { ...pole.position },
          direction: { ...pole.direction },
          length: pole.length,
        };
      });
      const newSquareLashings = squareLashings.map((lashing) => {
        return {
          identifier: lashing.identifier,
          fixedPoleIdentifier: lashing.fixedPole.identifier,
          loosePoleIdentifier: lashing.loosePole.identifier,
        };
      });
      const newBipodLashings = bipodLashings.map((lashing) => {
        return {
          identifier: lashing.identifier,
          pole1Identifier: lashing.pole1.identifier,
          pole2Identifier: lashing.pole2.identifier,
        };
      });
      const newTripodLashings = tripodLashings.map((lashing) => {
        return {
          identifier: lashing.identifier,
          leftPoleIdentifier: lashing.leftPole.identifier,
          middlePoleIdentifier: lashing.middlePole.identifier,
          rightPoleIdentifier: lashing.rightPole.identifier,
        };
      });
      const newScaffoldLashings = scaffoldLashings.map((lashing) => {
        return {
          identifier: lashing.identifier,
          pole1Identifier: lashing.pole1.identifier,
          pole2Identifier: lashing.pole2.identifier,
          offset: lashing.offset,
        };
      });

      addItems({
        variables: {
          projectId,
          poles: newPoles,
          squareLashings: newSquareLashings,
          bipodLashings: newBipodLashings,
          tripodLashings: newTripodLashings,
          scaffoldLashings: newScaffoldLashings,
        },
        optimisticResponse: {
          __typename: "AddItemsResponse",
          addItems: {
            ...project,
            poles: [...project.poles, ...newPoles],
            lashings: [...project.lashings, ...newSquareLashings],
            bipodLashings: [...project.bipodLashings, ...newBipodLashings],
            tripodLashings: [...project.tripodLashings, ...newTripodLashings],
            scaffoldLashings: [
              ...project.scaffoldLashings,
              ...newScaffoldLashings,
            ],
          },
        },
      });
    },
    [viewer]
  );

  const onLashingRelashed = useCallback(
    ({ lashing }: { lashing: Lashing }) => {
      if (lashing instanceof SquareLashing) {
        editLashing({
          variables: {
            projectId,
            lashing: {
              identifier: lashing.identifier,
              fixedPoleIdentifier: lashing.fixedPole.identifier,
              loosePoleIdentifier: lashing.loosePole.identifier,
            },
          },
        });
      } else if (lashing instanceof BipodLashing) {
        editBipodLashing({
          variables: {
            projectId,
            bipodLashing: {
              identifier: lashing.identifier,
              pole1Identifier: lashing.pole1.identifier,
              pole2Identifier: lashing.pole2.identifier,
            },
          },
        });
      } else if (lashing instanceof TripodLashing) {
        editTripodLashing({
          variables: {
            projectId,
            tripodLashings: {
              identifier: lashing.identifier,
              leftPoleIdentifier: lashing.leftPole.identifier,
              middlePoleIdentifier: lashing.middlePole.identifier,
              rightPoleIdentifier: lashing.rightPole.identifier,
            },
          },
        });
      } else if (lashing instanceof ScaffoldLashing) {
        editScaffoldLashing({
          variables: {
            projectId,
            scaffoldLashing: {
              identifier: lashing.identifier,
              pole1Identifier: lashing.pole1.identifier,
              pole2Identifier: lashing.pole2.identifier,
              offset: lashing.offset,
            },
          },
        });
      }
    },
    [viewer]
  );

  const onItemsRemoved = useCallback(
    ({
      poles: poles,
      squareLashings,
      bipodLashings,
      tripodLashings,
      scaffoldLashings,
    }: {
      poles: Pole[];
      squareLashings: SquareLashing[];
      bipodLashings: BipodLashing[];
      tripodLashings: TripodLashing[];
      scaffoldLashings: ScaffoldLashing[];
    }) => {
      const project = client.readFragment({
        fragment: PROJECT_FRAGMENT,
        id: `Project:${projectId}`,
        fragmentName: "Project",
      });
      if (!project) return;

      const poleIdentifiers = poles.map((pole) => pole.identifier);
      const squareLashingIdentifiers = squareLashings.map(
        (lashing) => lashing.identifier
      );
      const bipodLashingIdentifiers = bipodLashings.map(
        (lashing) => lashing.identifier
      );
      const tripodLashingIdentifiers = tripodLashings.map(
        (lashing) => lashing.identifier
      );
      const scaffoldLashingIdentifiers = scaffoldLashings.map(
        (lashing) => lashing.identifier
      );

      deleteItems({
        variables: {
          projectId,
          poleIdentifiers,
          squareLashingIdentifiers,
          bipodLashingIdentifiers,
          tripodLashingIdentifiers,
          scaffoldLashingIdentifiers,
        },
        optimisticResponse: {
          __typename: "DeleteItemsResponse",
          deleteItems: {
            ...project,
            poles: project.poles.filter(
              (p) => !poleIdentifiers.includes(p.identifier)
            ),
            lashings: project.lashings.filter(
              (l) => !squareLashingIdentifiers.includes(l.identifier)
            ),
            bipodLashings: project.bipodLashings.filter(
              (l) => !bipodLashingIdentifiers.includes(l.identifier)
            ),
            tripodLashings: project.tripodLashings.filter(
              (l) => !tripodLashingIdentifiers.includes(l.identifier)
            ),
            scaffoldLashings: project.scaffoldLashings.filter(
              (l) => !scaffoldLashingIdentifiers.includes(l.identifier)
            ),
          },
        },
      });
    },
    [viewer]
  );

  useEffect(() => {
    if (!viewer) return;
    (viewer.scene as any).addEventListener("new_items_placed", onItemsPlaced);
    return () => {
      (viewer.scene as any).removeEventListener(
        "new_items_placed",
        onItemsPlaced
      );
    };
  }, [onItemsPlaced, viewer]);

  useEffect(() => {
    if (!viewer) return;
    (viewer.scene as any).addEventListener(
      "lashing_relashed",
      onLashingRelashed
    );
    return () => {
      (viewer.scene as any).removeEventListener(
        "lashing_relashed",
        onLashingRelashed
      );
    };
  }, [onLashingRelashed, viewer]);

  useEffect(() => {
    if (!viewer) return;
    (viewer.scene as any).addEventListener("items_removed", onItemsRemoved);
    return () => {
      (viewer.scene as any).removeEventListener(
        "items_removed",
        onItemsRemoved
      );
    };
  }, [onItemsRemoved, viewer]);
};
